<template>
  <v-app>
    <navigation-drawer>
      <template #navigation>
        <router-link :to="{ name: RouteNames.Home }">
          <e-icon name="backArrow" class="ml-2 back-arrow" />
        </router-link>
        <div class="pt-2">
          <menu-item v-for="(item, index) in menu" :key="index" :item="item" :disabled="!isActiveStatus" />
        </div>
        <v-spacer />
        <menu-item v-for="(item, index) in bottomMenu" :key="index" :item="item" />
      </template>

      <template
        v-if="accommodationObject && !accommodationObject?.contracts?.length && !isUnknownStatus && !isDeclinedStatus"
        #notification
      >
        <e-alert closable>
          <div class="paragraph-m">
            <b
              >Для запуска продаж необходимо создать аккаунт, для этого нажмите "Создать аккаунт". Далее создайте
              вручную тарифный план или сделайте импорт из менеджера каналов</b
            >
          </div>
          <div class="paragraph-s text-secondary mt-1">А пока вы можете заполнить информацию об отеле</div>
        </e-alert>
      </template>
      <template
        v-else-if="
          accommodationObject?.commonInformation?.status === ApproveStatus.Approved &&
          activeAccount?.status === ContractStatus.Draft
        "
        #notification
      >
        <e-alert closable>
          <div class="paragraph-m">
            <b>Для активации аккаунта необходим действующий тарифный план</b>
          </div>
          <div class="paragraph-s text-secondary mt-1">
            Создайте новый тарифный план и отправьте его на модерацию или подключите менеджер каналов
          </div>
        </e-alert>
      </template>
      <template
        v-else-if="isTariffPage($route) && activeTariff && activeTariff.status === TariffScheduleStatus.Draft"
        #notification
      >
        <e-alert closable>
          <div class="paragraph-m">
            <b>Завершите настройку тарифного плана для запуска продаж после модерации</b>
          </div>
          <div class="paragraph-s_h text-secondary mt-1">
            Убедитесь, что основные настройки произведены, выбрана хотя бы одна группа услуг и&nbsp;правильно определена
            квота номеров. В&nbsp;случае возникновения сложности с&nbsp;настройками просьба
            обратиться&nbsp;в&nbsp;службу
            <router-link class="link" :to="{ name: RouteNames.Support }">Поддержки</router-link>.
          </div>
        </e-alert>
      </template>
      <template #header>
        <template v-if="$vuetify.display.mdAndDown">
          <e-dialog ref="dialogRef" left transparent-content full-with-on-tablet>
            <template #activator>
              <e-icon class="mr-4" name="burger" />
            </template>

            <template #title>
              <div class="d-flex align-center">
                <e-icon class="mr-4" name="burger" />
                <div>
                  <div class="input-placeholder">
                    {{ accommodationObject?.commonInformation?.name[0]?.value }}
                  </div>
                  <div v-if="activeAccount?.contractNumber" class="d-flex align-center">
                    <div class="paragraph-xs">Аккаунт: {{ activeAccount.contractNumber }}</div>
                    <account-status size="S" class="ml-1" :status="activeAccount.status" />
                  </div>
                </div>
              </div>
            </template>

            <account-layout-select v-if="accommodationObject" class="account-layout__object mb-3" />
            <skeleton-loader v-else width="100%" height="54px" class="mb-3" />

            <create-account-dialog>
              <template #activator>
                <e-btn prepend-icon-src="plus" variant="link" class="ml-2 mb-4"> Создать аккаунт</e-btn>
              </template>
            </create-account-dialog>

            <router-link :to="{ name: RouteNames.Home }">
              <e-icon name="backArrow" class="ml-2 back-arrow" />
            </router-link>

            <menu-item
              v-for="(item, index) in menu"
              :key="index"
              :item="item"
              :disabled="!isActiveStatus"
              @click="closeDialog"
            />

            <menu-item
              v-for="(item, index) in bottomMenu"
              :key="'bottom-' + index"
              :item="item"
              @click="closeDialog"
            />
          </e-dialog>
          <div>
            <div class="input-placeholder">
              {{ accommodationObject?.commonInformation?.name[0]?.value }}
            </div>
            <div v-if="activeAccount?.contractNumber" class="d-flex align-center">
              <div class="paragraph-xs">Аккаунт: {{ activeAccount.contractNumber }}</div>
              <account-status v-if="activeAccount" size="S" class="ml-1" :status="activeAccount.status" />
            </div>
          </div>
        </template>
        <template v-else>
          <account-layout-select v-if="accommodationObject" class="account-layout__object" />
          <skeleton-loader v-else width="400px" height="58px" />

          <create-account-dialog :disabled="isAnyResourceEdit">
            <template #activator>
              <e-btn prepend-icon-src="plus" variant="link" class="ml-4" :disabled="isAnyResourceEdit">
                Создать аккаунт</e-btn
              >
            </template>
          </create-account-dialog>
        </template>
        <v-spacer />
        <default-layout-header />
      </template>
      <template #main>
        <error-boundary error-key="accommodationObject2">
          <slot />
        </error-boundary>
      </template>
    </navigation-drawer>
    <tour-layout />
  </v-app>
</template>

<script lang="ts" setup>
import { ref, toRef } from 'vue';
import { useRoute } from 'vue-router';
import type { IDialog, MenuItemType } from '@/core/types/components/components';
import SkeletonLoader from '@/core/components/interface/SkeletonLoader.vue';
import EDialog from '@/core/components/modals/Dialog.vue';
import { EAlert, EBtn, EIcon } from '@/core/components/ui';
import { useCommonLockResourceStore } from '@/core/composables/useLockResource';
import { getConfigValue } from '@/core/consts/config';
import MenuItem from '@/core/layouts/components/MenuItem.vue';
import NavigationDrawer from '@/core/layouts/components/NavigationDrawer.vue';
import TourLayout from '@/core/layouts/components/TourLayout.vue';
import AccountLayoutSelect from '@/core/layouts/components/account/AccountLayoutSelect.vue';
import DefaultLayoutHeader from '@/core/layouts/components/default/DefaultLayoutHeader.vue';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { ApproveStatus, ContractStatus, PermissionTarget, TariffScheduleStatus } from '@/graphql/generated';
import ErrorBoundary from '@/parts/errors/components/ErrorBoundary.vue';
import AccountStatus from '@/parts/objects/components/AccountStatus.vue';
import CreateAccountDialog from '@/parts/objects/components/CreateAccountDialog.vue';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';
import { useTariffsStore } from '@/parts/tariffs/store/tariffs';
import { isTariffPage } from '@/parts/tariffs/utils/tariffs';

const dialogRef = ref<IDialog>();

const closeDialog = () => {
  dialogRef.value?.close();
};

const $route = useRoute();

const { getActiveObject, accommodationObject, isUnknownStatus, isDeclinedStatus, isActiveStatus, activeAccount } =
  useAccommodationObjectsStore();
const { activeTariff } = useTariffsStore();
const { isAnyResourceEdit } = useCommonLockResourceStore();

const menu = toRef<() => MenuItemType[]>(() => [
  {
    title: 'Главная',
    icon: 'dashboard',
    to: {
      name: RouteNames.ObjectInfo,
      params: {
        id: $route.params.id,
        acc_id: $route.params.acc_id,
      },
    },
    permissions: [PermissionTarget.AccommodationObjects],
  },
  {
    title: 'Общие настройки',
    icon: 'settings',
    to: {
      name: RouteNames.ObjectSettings,
      params: {
        id: $route.params.id,
        acc_id: $route.params.acc_id,
      },
    },
    permissions: [PermissionTarget.AccommodationObjects],
  },
  {
    title: 'Категории номеров',
    icon: 'room',
    to: {
      name: RouteNames.RoomCategories,
      params: {
        id: $route.params.id,
        acc_id: $route.params.acc_id,
      },
    },
    permissions: [PermissionTarget.RoomCategories],
  },
  {
    title: 'Контент',
    icon: 'contentsOR',
    to: {
      name: RouteNames.ObjectContent,
      params: {
        id: $route.params.id,
        acc_id: $route.params.acc_id,
      },
    },
    permissions: [PermissionTarget.AccommodationObjectsContent],
  },
  {
    title: 'Аналитика',
    icon: 'graph',
    to: {
      name: RouteNames.ObjectAnalytics,
      params: {
        id: $route.params.id,
        acc_id: $route.params.acc_id,
      },
    },
    permissions: [PermissionTarget.AccommodationObjects],
    hidden: !getConfigValue('analyticsPageEnabled'),
  },
  {
    title: 'Тарифные планы',
    id: 'tariffs-menu-item',
    icon: 'tariff',
    permissions: [PermissionTarget.TariffSchedules],
    children: [
      {
        title: 'Ручное управление',
        active:
          $route.name === RouteNames.TariffsControlled ||
          ($route.matched.some((r) => r.name === RouteNames.Tariff) &&
            !!activeTariff.value &&
            !activeTariff.value.remoteInteraction),
        to: {
          name: RouteNames.TariffsControlled,
          params: {
            id: $route.params.id,
            acc_id: $route.params.acc_id,
          },
        },
      },
      {
        title: 'Менеджер каналов',
        active:
          $route.name === RouteNames.CMList ||
          ($route.matched.some((r) => r.name === RouteNames.Tariff) &&
            !!activeTariff.value &&
            !!activeTariff.value.remoteInteraction),
        to: {
          name: RouteNames.CMList,
          params: {
            id: $route.params.id,
            acc_id: $route.params.acc_id,
          },
        },
      },
    ],
  },
  {
    title: 'Бронирования',
    icon: 'keyBook',
    lockedIcon: 'bookingLock',
    to: {
      name: RouteNames.Books,
      params: {
        id: $route.params.id,
        acc_id: $route.params.acc_id,
      },
    },
    permissions: [PermissionTarget.Orders],
  },
  {
    title: 'Сверка',
    icon: 'documentCheck',
    lockedIcon: 'documentCheckBlock',
    to: {
      name: RouteNames.Reconciliation,
      params: {
        id: $route.params.id,
        acc_id: $route.params.acc_id,
      },
    },
    withBadge: false,
    permissions: [PermissionTarget.OrderReconciliation],
    hidden: !getConfigValue('orderReconciliationEnabled')
  },
]);

const bottomMenu: MenuItemType[] = [
  {
    title: 'Поддержка',
    icon: 'support',
    to: {
      name: RouteNames.Support,
    },
  },
];

await getActiveObject();
</script>

<style lang="scss" scoped>
.account-layout {
  &__object {
    width: 100%;
    max-width: 400px;

    @include md-and-down {
      max-width: 100%;
    }
  }
}

.back-arrow {
  color: $text-secondary-color;

  @include hover {
    color: $primary-color;
  }
}
</style>
